import { Button, DatePicker, Modal, Select, Skeleton, Space, Tabs, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next"; // Added import for translations
import PropTypes from "prop-types";
import { FaRegFilePdf } from "react-icons/fa6";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import uploadFile from "../../../../utilities/uploadFile";
import { US_STATES } from "../../../../constants/usStates";

const validateVIN = (vin) => {
    const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

    if (!vin || vin.trim() === "") {
        return { isValid: false, error: "VIN is required" };
    }

    const cleanVIN = vin.trim().toUpperCase();

    if (cleanVIN.length !== 17) {
        return {
            isValid: false,
            error: "VIN must be exactly 17 characters long",
        };
    }

    if (!VIN_PATTERN.test(cleanVIN)) {
        return {
            isValid: false,
            error: "VIN contains invalid characters. Only A-H, J-N, P-R, Z, and 0-9 are allowed",
        };
    }

    return { isValid: true, cleanVIN };
};

const getRegistrationState = (registration) => {
    const state = registration?.state?.trim();
    if (state && state.length === 2) {
        return state.toUpperCase();
    }
    return null;
};

const CarDocumentsSection = ({
    car,
    isLoading = false,
    currentLocation = null,
    isInsuranceEnabled = false,
    isAbiInsuranceEnabled = false,
}) => {
    const { t } = useTranslation("cars"); // Added translation hook

    const items = [
        {
            key: "1",
            label: t("carDetail.insurance"),
            children: (
                <InsuranceTab
                    car={car}
                    isLoading={isLoading}
                    currentLocation={currentLocation}
                    isInsuranceEnabled={isInsuranceEnabled}
                    isAbiInsuranceEnabled={isAbiInsuranceEnabled}
                />
            ),
        },
        {
            key: "2",
            label: t("carDetail.registration"),
            children: <RegistrationTab car={car} />,
        },
        // {
        //   key: "3",
        //   label: t("carDetail.inspection"),
        //   children: <InspectionTab car={car} />,
        // },
    ];

    const onChange = () => {};

    return (
        <div className="w-full rounded-2xl border border-[#f0f0f0] bg-white p-4">
            {isLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <Tabs
                    tabBarStyle={{
                        marginBottom: 0,
                    }}
                    defaultActiveKey="1"
                    items={items}
                    onChange={onChange}
                />
            )}
        </div>
    );
};

CarDocumentsSection.propTypes = {
    car: PropTypes.shape({
        id: PropTypes.string,
        VIN: PropTypes.string,
        insurance: PropTypes.shape({
            company: PropTypes.string,
            policyNo: PropTypes.string,
            coverage: PropTypes.string,
            exp: PropTypes.string,
            doc: PropTypes.string,
        }),
        insuranceVehicleInfo: PropTypes.shape({
            active: PropTypes.bool,
        }),
        abiVehicleInfo: PropTypes.shape({
            registeredOnPeriodX: PropTypes.bool,
            registeredAt: PropTypes.string,
            locationId: PropTypes.number,
        }),
        registration: PropTypes.shape({
            state: PropTypes.string,
            exp: PropTypes.string,
            doc: PropTypes.string,
        }),
    }),
    isLoading: PropTypes.bool,
    currentLocation: PropTypes.shape({
        locationId: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        isInsuranceEnabled: PropTypes.bool,
    }),
    isInsuranceEnabled: PropTypes.bool,
    isAbiInsuranceEnabled: PropTypes.bool,
};

export default CarDocumentsSection;

const InsuranceTab = ({
    car,
    isLoading = false,
    currentLocation = null,
    isInsuranceEnabled = false,
    isAbiInsuranceEnabled = false,
}) => {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const [editingInsurance, setEditingInsurance] = useState(false);
    const [updatingInsurance, setUpdatingInsurance] = useState(false);
    const [insuranceDoc, setInsuranceDoc] = useState(null);
    const [isLinking, setIsLinking] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [showUnlinkModal, setShowUnlinkModal] = useState(false);
    const [isAddingToAbi, setIsAddingToAbi] = useState(false);
    const [showAbiRegistrationModal, setShowAbiRegistrationModal] = useState(false);
    const [abiRegistrationState, setAbiRegistrationState] = useState(null);
    const [isRemovingFromAbi, setIsRemovingFromAbi] = useState(false);
    const [showRemoveAbiModal, setShowRemoveAbiModal] = useState(false);
    const [abiIneffectiveDate, setAbiIneffectiveDate] = useState(() => dayjs());

    const insuranceForm = useFormik({
        initialValues: car?.insurance || {
            company: "",
            policyNo: "",
            coverage: "",
            exp: "",
            doc: "",
        },
        onSubmit: async (values) => {
            if (updatingInsurance) return;

            setUpdatingInsurance(true);

            // Replace Firestore update with Axios API call
            const { ...rest } = values;
            const payload = { ...rest };

            try {
                if (insuranceDoc) {
                    const docFile = await uploadFile(insuranceDoc);
                    if (docFile) {
                        payload.doc = docFile;
                    }
                }
                const response = await apiClient.patch(`/api/car/${car.id}`, {
                    insurance: payload,
                });
                if (response.data.success) {
                    // Create a copy instead of modifying directly
                    const updatedCar = { ...car, insurance: payload };
                    Object.assign(car, updatedCar);
                    setEditingInsurance(false);
                    toast.success({
                        message: t("success"),
                        description: t("carDetail.insuranceUpdated"),
                        duration: 2,
                    });
                    setUpdatingInsurance(false);
                }

                // Handle the success response
                setUpdatingInsurance(false);

                // window.location.reload(false); // Reload the page if necessary
            } catch (_error) {
                toast.error({
                    message: t("carDetail.errorUpdatingDocument"),
                    description: _error.message,
                    duration: 2,
                });
                setUpdatingInsurance(false);
            }
        },
        enableReinitialize: true,
    });

    const handleInsuranceLink = async () => {
        // Check if company has teqmobility insurance enabled
        if (!isInsuranceEnabled) {
            toast.warning({
                message: t("carDetail.companyInsuranceDisabled"),
                description: t("carDetail.companyInsuranceDisabledDescription"),
                duration: 5,
            });
            return;
        }

        // Check if current location has insurance enabled
        if (currentLocation && currentLocation.isInsuranceEnabled === false) {
            toast.warning({
                message: t("carDetail.locationInsuranceWarning"),
                description: t("carDetail.locationInsuranceWarningDescription", {
                    location: `${currentLocation.label}`,
                }),
                duration: 5,
            });
            return;
        }

        setShowLinkModal(true);
    };

    const handleConfirmLink = async () => {
        if (isLinking) return;

        const vinValidation = validateVIN(car.VIN);

        if (!vinValidation.isValid) {
            toast.error({
                message: t("error"),
                description: vinValidation.error,
                duration: 2,
            });
            return;
        }

        try {
            setIsLinking(true);
            setShowLinkModal(false);

            // API call to add vehicle to TeqMobility insurance
            const response = await apiClient.post(`/api/insurance/add-vehicle-to-teqmobility/${car.id}`, {
                VIN: vinValidation.cleanVIN,
            });

            if (response.data.success) {
                if (response.data.data?.insuranceVehicleInfo) {
                    Object.assign(car, {
                        insuranceVehicleInfo: response.data.data.insuranceVehicleInfo,
                    });
                }

                toast.success({
                    message: t("success"),
                    description: t("carDetail.vehicleLinkedToInsurance"),
                    duration: 2,
                });
            }
        } catch (error) {
            toast.error({
                message: t("error"),
                description: error.response?.data?.message || t("carDetail.errorLinkingVehicle"),
            });

            // console.log("Add car to insurance error", error.response.data);
        } finally {
            setIsLinking(false);
        }
    };

    const handleCancelLink = () => {
        setShowLinkModal(false);
    };

    const handleUnlinkVehicle = async () => {
        setShowUnlinkModal(true);
    };

    const handleConfirmUnlink = async () => {
        if (isUnlinking) return;

        try {
            setIsUnlinking(true);
            setShowUnlinkModal(false);

            // API call to remove vehicle from TeqMobility insurance
            const response = await apiClient.delete(`/api/insurance/remove-vehicle-from-teqmobility/${car.id}`);

            if (response.data.success) {
                // Update the car object to reflect the unlinked state
                Object.assign(car, {
                    insuranceVehicleInfo: { active: false },
                });

                toast.success({
                    message: t("success"),
                    description: t("carDetail.vehicleUnlinkedFromInsurance"),
                    duration: 2,
                });
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || t("carDetail.errorUnlinkingVehicle");
            toast.error({
                message: t("error"),
                description: errorMessage,
                duration: 5,
            });
        } finally {
            setIsUnlinking(false);
        }
    };

    const handleCancelUnlink = () => {
        setShowUnlinkModal(false);
    };

    const mergeAbiResponseIntoCar = (data) => {
        if (!data) return;
        const { abiVehicleInfo, ...rest } = data;
        Object.assign(car, rest);
        if (abiVehicleInfo) {
            Object.assign(car, {
                abiVehicleInfo: { ...car.abiVehicleInfo, ...abiVehicleInfo },
            });
        }
    };

    const callAddToAbi = async (vin, registrationState) => {
        const response = await apiClient.post("/api/insurance/add-car-to-abi", {
            vin,
            registrationState,
        });

        if (response.data.success) {
            mergeAbiResponseIntoCar(response.data.data);
            toast.success({
                message: t("success"),
                description: t("carDetail.vehicleAddedToAbi"),
                duration: 2,
            });
        }

        return response;
    };

    const handleAddToAbi = async () => {
        if (!isAbiInsuranceEnabled) {
            toast.warning({
                message: t("carDetail.companyAbiDisabled"),
                duration: 5,
            });
            return;
        }

        const vinValidation = validateVIN(car.VIN);
        if (!vinValidation.isValid) {
            toast.error({
                message: t("error"),
                description: vinValidation.error,
                duration: 2,
            });
            return;
        }

        const registrationState = getRegistrationState(car.registration);
        if (registrationState) {
            try {
                setIsAddingToAbi(true);
                await callAddToAbi(vinValidation.cleanVIN, registrationState);
            } catch (error) {
                toast.error({
                    message: t("error"),
                    description: error.response?.data?.message || t("carDetail.errorAddingToAbi"),
                    duration: 5,
                });
            } finally {
                setIsAddingToAbi(false);
            }
            return;
        }

        setAbiRegistrationState(null);
        setShowAbiRegistrationModal(true);
    };

    const handleCancelAbiRegistration = () => {
        setShowAbiRegistrationModal(false);
        setAbiRegistrationState(null);
    };

    const handleConfirmAbiRegistration = async () => {
        if (isAddingToAbi) return;

        if (!abiRegistrationState) {
            toast.error({
                message: t("error"),
                description: t("carDetail.registrationStateRequired"),
                duration: 2,
            });
            return;
        }

        const vinValidation = validateVIN(car.VIN);
        if (!vinValidation.isValid) {
            toast.error({
                message: t("error"),
                description: vinValidation.error,
                duration: 2,
            });
            return;
        }

        try {
            setIsAddingToAbi(true);

            const patchResponse = await apiClient.patch(`/api/car/${car.id}`, {
                registration: {
                    ...car.registration,
                    state: abiRegistrationState,
                },
            });

            if (patchResponse.data.success) {
                Object.assign(car, {
                    registration: {
                        ...car.registration,
                        state: abiRegistrationState,
                    },
                });
            }

            setShowAbiRegistrationModal(false);
            await callAddToAbi(vinValidation.cleanVIN, abiRegistrationState);
        } catch (error) {
            toast.error({
                message: t("error"),
                description: error.response?.data?.message || t("carDetail.errorAddingToAbi"),
                duration: 5,
            });
        } finally {
            setIsAddingToAbi(false);
            setAbiRegistrationState(null);
        }
    };

    const handleRemoveFromAbi = () => {
        setAbiIneffectiveDate(dayjs());
        setShowRemoveAbiModal(true);
    };

    const handleConfirmRemoveFromAbi = async () => {
        if (isRemovingFromAbi) return;

        try {
            setIsRemovingFromAbi(true);
            setShowRemoveAbiModal(false);

            const response = await apiClient.post("/api/insurance/remove-car-from-abi", {
                carId: car.id,
                ineffectiveDate: abiIneffectiveDate
                    ? abiIneffectiveDate.format("MM/DD/YYYY")
                    : undefined,
            });

            if (response.data.success) {
                mergeAbiResponseIntoCar(response.data.data);
                if (!response.data.data?.abiVehicleInfo) {
                    Object.assign(car, {
                        abiVehicleInfo: {
                            ...car.abiVehicleInfo,
                            registeredOnPeriodX: false,
                        },
                    });
                }

                toast.success({
                    message: t("success"),
                    description: t("carDetail.vehicleRemovedFromAbi"),
                    duration: 2,
                });
            }
        } catch (error) {
            toast.error({
                message: t("error"),
                description: error.response?.data?.message || t("carDetail.errorRemovingFromAbi"),
                duration: 5,
            });
        } finally {
            setIsRemovingFromAbi(false);
        }
    };

    const handleCancelRemoveFromAbi = () => {
        setShowRemoveAbiModal(false);
        setAbiIneffectiveDate(dayjs());
    };

    const isAbiRegistered = car?.abiVehicleInfo?.registeredOnPeriodX === true;

    const renderAbiStatus = () => {
        if (isAbiRegistered) {
            return (
                <Tag color="success">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#0A8754] animate-pulse" />
                        <span className="text-[#0A8754] animate-pulse font-medium">{t("carDetail.abiRegistered")}</span>
                    </div>
                </Tag>
            );
        }

        return (
            <Tag color="default">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                    <span className="text-gray-600 font-medium">{t("carDetail.abiNotRegistered")}</span>
                </div>
            </Tag>
        );
    };

    const renderInsuranceStatus = () => {
        const isLinked = car?.insuranceVehicleInfo?.active || false;

        if (isLinked) {
            return (
                <Tooltip title={t("carDetail.insuranceLinkedActive")}>
                    <Tag color="success">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-[#0A8754] animate-pulse" />
                            <span className="text-[#0A8754] animate-pulse font-medium">{t("carDetail.linked")}</span>
                        </div>
                    </Tag>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={t("carDetail.insuranceNotLinked")}>
                <Tag color="default">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                        <span className="text-gray-600 font-medium">{t("carDetail.notLinked")}</span>
                    </div>
                </Tag>
            </Tooltip>
        );
    };

    const statusRowClassName =
        "grid grid-cols-1 items-center gap-4 sm:grid-cols-[minmax(5.5rem,auto)_minmax(0,1fr)_auto]";

    return (
        <>
            <div className="flex flex-col gap-4 py-1">
                {isInsuranceEnabled && (
                    <div className={statusRowClassName}>
                        <div className="fontSemi shrink-0">{t("carDetail.insurance")}</div>
                        <div className="min-w-0">{!isLoading && renderInsuranceStatus()}</div>
                        <Space size={12} wrap className="shrink-0 sm:justify-self-end">
                            {editingInsurance ? (
                                <>
                                    <Button
                                        shape="round"
                                        size="small"
                                        onClick={() => {
                                            setInsuranceDoc(null);
                                            insuranceForm.setValues(
                                                car?.insurance || {
                                                    company: "",
                                                    policyNo: "",
                                                    coverage: "",
                                                    exp: "",
                                                    doc: "",
                                                },
                                            );
                                            setEditingInsurance(false);
                                        }}>
                                        {t("rates.cancel")}
                                    </Button>
                                    <Button
                                        type="primary"
                                        shape="round"
                                        size="small"
                                        loading={updatingInsurance}
                                        onClick={insuranceForm.handleSubmit}>
                                        {t("rates.save")}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {!isLoading && !car?.insuranceVehicleInfo?.active && (
                                        <Button
                                            type="primary"
                                            shape="round"
                                            size="small"
                                            loading={isLinking}
                                            onClick={handleInsuranceLink}>
                                            {t("rates.link")}
                                        </Button>
                                    )}

                                    {!isLoading && car?.insuranceVehicleInfo?.active && (
                                        <Button
                                            shape="round"
                                            size="small"
                                            danger
                                            loading={isUnlinking}
                                            onClick={handleUnlinkVehicle}>
                                            {t("rates.unlink")}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Space>
                    </div>
                )}

                {isAbiInsuranceEnabled && (
                    <div className={statusRowClassName}>
                        <div className="fontSemi shrink-0">INSHUR</div>
                        <div className="min-w-0">{!isLoading && renderAbiStatus()}</div>
                        <Space size={12} wrap className="shrink-0 sm:justify-self-end">
                            {!isLoading && !isAbiRegistered && (
                                <Button
                                    type="primary"
                                    shape="round"
                                    size="small"
                                    loading={isAddingToAbi}
                                    onClick={handleAddToAbi}>
                                    {t("carDetail.addToAbi")}
                                </Button>
                            )}

                            {!isLoading && isAbiRegistered && (
                                <Button
                                    shape="round"
                                    size="small"
                                    danger
                                    loading={isRemovingFromAbi}
                                    onClick={handleRemoveFromAbi}>
                                    {t("carDetail.removeFromAbi")}
                                </Button>
                            )}
                        </Space>
                    </div>
                )}
            </div>
            <div className="mt-4 flex flex-row justify-between">
                {editingInsurance ? (
                    <form
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            fontSize: "0.9rem",
                            justifyContent: "space-between",
                        }}
                        onSubmit={insuranceForm.handleSubmit}>
                        <table className="fontRegular" style={{ width: "65%" }}>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("company")}: </span>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        name="company"
                                        onChange={insuranceForm.handleChange}
                                        value={insuranceForm.values?.company}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            backgroundColor: "#f2f2fa",
                                            outline: "none",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.policyNo")}: </span>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        name="policyNo"
                                        onChange={insuranceForm.handleChange}
                                        value={insuranceForm.values?.policyNo}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            backgroundColor: "#f2f2fa",
                                            outline: "none",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.coverage")}: </span>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        name="coverage"
                                        onChange={insuranceForm.handleChange}
                                        value={insuranceForm.values?.coverage}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            backgroundColor: "#f2f2fa",
                                            outline: "none",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.expDate")}:&nbsp;&nbsp;</span>
                                </td>
                                <td>
                                    <input
                                        type="date"
                                        name="exp"
                                        onChange={insuranceForm.handleChange}
                                        value={insuranceForm.values?.exp || undefined}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            outline: "none",
                                            backgroundColor: "#f2f2fa",
                                            border: 0,
                                            borderRadius: "0.5rem",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginTop: "10px",
                                            marginBottom: "5px",
                                            gap: "5px",
                                        }}
                                        onClick={() => {
                                            const butt1 = document.getElementById("insuranceInput");
                                            butt1.click();
                                        }}
                                        onKeyUp={(e) => {
                                            if (e.key === "Enter") {
                                                const butt1 = document.getElementById("insuranceInput");
                                                butt1.click();
                                            }
                                        }}>
                                        {" "}
                                        <input
                                            type="file"
                                            id="insuranceInput"
                                            className="file-input-scan-copy"
                                            accept="application/pdf"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                if (e.target.files?.length) {
                                                    setInsuranceDoc(e.target.files[0]);
                                                }
                                            }}
                                        />
                                        <div
                                            className="updatebtn"
                                            style={{
                                                marginRight: "0.5rem",
                                                fontSize: "10px",
                                            }}>
                                            {t("carDetail.scanCopy")}
                                        </div>
                                        <span>
                                            {insuranceDoc ? t("carDetail.fileSelected") : t("carDetail.uploadFile")}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </form>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            fontSize: "0.9rem",
                            justifyContent: "space-between",
                        }}>
                        {/* <table
                            className="fontRegular"
                            style={{
                                fontSize: "0.9rem",
                                width: "65%",
                                marginTop: "0.3rem",
                            }}>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("company")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>{car?.insurance?.company}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.policyNo")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>{car?.insurance?.policyNo}</span>
                                </td>
                            </tr>

                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.coverage")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>{car?.insurance?.coverage}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.expDate")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>
                                        {car?.insurance?.exp ? new Date(car?.insurance?.exp).toLocaleDateString() : ""}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                {car?.insurance && (
                                    <td>
                                        <a
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                textDecoration: "none",
                                            }}
                                            href={car?.insurance?.doc}
                                            target="_blank"
                                            rel="noreferrer">
                                            <div className="fontRegular" style={{ fontSize: "0.8rem" }}>
                                                {t("carDetail.downloadPdf")}
                                            </div>
                                            &nbsp;&nbsp; <img src={Downicon} style={{ height: "0.8rem" }} alt="" />
                                        </a>
                                    </td>
                                )}
                            </tr>
                        </table> */}
                    </div>
                )}
            </div>
            {/* Link Confirmation Modal */}
            <Modal
                title={t("carDetail.confirmVehicleLink")}
                open={showLinkModal}
                onCancel={handleCancelLink}
                maskClosable={false}
                centered
                width={400}
                footer={[
                    <Button
                        key="cancel"
                        shape="round"
                        onClick={handleCancelLink}
                        style={{
                            marginRight: "8px",
                        }}>
                        {t("rates.cancel")}
                    </Button>,
                    <Button key="confirm" shape="round" type="primary" onClick={handleConfirmLink} loading={isLinking}>
                        {isLinking ? t("rates.saving") : t("carDetail.confirmLink")}
                    </Button>,
                ]}>
                <p style={{ color: "#666", marginBottom: "1rem" }}>{t("carDetail.confirmVehicleLinkDescription")}</p>
            </Modal>
            {/* Unlink Confirmation Modal */}
            <Modal
                title={t("carDetail.confirmVehicleUnlink")}
                open={showUnlinkModal}
                onCancel={handleCancelUnlink}
                maskClosable={false}
                centered
                width={400}
                footer={[
                    <Button
                        key="cancel"
                        shape="round"
                        onClick={handleCancelUnlink}
                        style={{
                            marginRight: "8px",
                        }}>
                        {t("rates.cancel")}
                    </Button>,
                    <Button
                        key="confirm"
                        shape="round"
                        type="primary"
                        danger
                        onClick={handleConfirmUnlink}
                        loading={isUnlinking}>
                        {isUnlinking ? t("rates.saving") : t("carDetail.confirmUnlink")}
                    </Button>,
                ]}>
                <p style={{ color: "#666", marginBottom: "1rem" }}>{t("carDetail.confirmVehicleUnlinkDescription")}</p>
            </Modal>
            <Modal
                title={t("carDetail.selectRegistrationState")}
                open={showAbiRegistrationModal}
                onCancel={handleCancelAbiRegistration}
                maskClosable={false}
                centered
                width={400}
                footer={[
                    <Button
                        key="cancel"
                        shape="round"
                        onClick={handleCancelAbiRegistration}
                        style={{
                            marginRight: "8px",
                        }}>
                        {t("rates.cancel")}
                    </Button>,
                    <Button
                        key="confirm"
                        shape="round"
                        type="primary"
                        onClick={handleConfirmAbiRegistration}
                        loading={isAddingToAbi}>
                        {isAddingToAbi ? t("rates.saving") : t("carDetail.addToAbi")}
                    </Button>,
                ]}>
                <p style={{ color: "#666", marginBottom: "1rem" }}>{t("carDetail.confirmAddToAbiDescription")}</p>
                <div style={{ marginBottom: "0.5rem" }}>
                    <span style={{ display: "block", marginBottom: "0.5rem", color: "#666" }}>
                        {t("carDetail.selectRegistrationState")}
                    </span>
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder={t("carDetail.selectRegistrationState")}
                        options={US_STATES}
                        value={abiRegistrationState}
                        onChange={setAbiRegistrationState}
                    />
                </div>
            </Modal>
            <Modal
                title={t("carDetail.confirmRemoveFromAbi")}
                open={showRemoveAbiModal}
                onCancel={handleCancelRemoveFromAbi}
                maskClosable={false}
                centered
                width={400}
                footer={[
                    <Button
                        key="cancel"
                        shape="round"
                        onClick={handleCancelRemoveFromAbi}
                        style={{
                            marginRight: "8px",
                        }}>
                        {t("rates.cancel")}
                    </Button>,
                    <Button
                        key="confirm"
                        shape="round"
                        type="primary"
                        danger
                        onClick={handleConfirmRemoveFromAbi}
                        loading={isRemovingFromAbi}>
                        {isRemovingFromAbi ? t("rates.saving") : t("carDetail.removeFromAbi")}
                    </Button>,
                ]}>
                <p style={{ color: "#666", marginBottom: "1rem" }}>{t("carDetail.confirmRemoveFromAbiDescription")}</p>
                <div>
                    <span style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>
                        {t("carDetail.abiIneffectiveDate")}
                    </span>
                    <DatePicker
                        className="w-full"
                        format="MM/DD/YYYY"
                        value={abiIneffectiveDate}
                        onChange={(value) => setAbiIneffectiveDate(value || dayjs())}
                    />
                </div>
            </Modal>
        </>
    );
};

const RegistrationTab = ({ car }) => {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const [editingRegistration, setEditingRegistration] = useState(false);
    const [updatingRegistration, setUpdatingRegistration] = useState(false);
    const [registrationDoc, setRegistrationDoc] = useState(null);

    const regForm = useFormik({
        initialValues: car?.registration || { state: "", exp: "", doc: "" },
        onSubmit: async (values) => {
            if (updatingRegistration) return;

            setUpdatingRegistration(true);

            const { ...rest } = values;
            const payload = { ...rest };

            try {
                if (registrationDoc) {
                    const docFile = await uploadFile(registrationDoc);
                    if (docFile) {
                        payload.doc = docFile;
                    }
                }
                const response = await apiClient.patch(`/api/car/${car.id}`, {
                    registration: payload,
                });

                if (response.data.success) {
                    // Create a copy instead of modifying directly
                    const updatedCar = { ...car, registration: payload };
                    Object.assign(car, updatedCar);
                    setUpdatingRegistration(false);

                    toast.success({
                        message: t("success"),
                        description: t("carDetail.registrationUpdated"),
                        duration: 2,
                    });
                    setEditingRegistration(false);
                }
                setUpdatingRegistration(false);
            } catch (_error) {
                toast.error({
                    message: t("error"),
                    description: t("carDetail.errorUpdatingDocument"),
                    duration: 2,
                });

                // Error updating document info
                setUpdatingRegistration(false);
            }
        },
        enableReinitialize: true,
    });

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginTop: "0.5rem",
                }}>
                <div className="fontSemi">{t("carDetail.carRegistration")}</div>
                {/* <div style={{ display: "flex", justifyContent: "center" }}>
                    {editingRegistration ? (
                        <>
                            <div
                                className="cancelbtn fontRegular"
                                style={{
                                    marginRight: "0.5rem",
                                    fontSize: "10px",
                                }}
                                onClick={() => {
                                    setRegistrationDoc(null);
                                    setEditingRegistration(false);
                                    regForm.setValues(car?.registration || { state: "", exp: "", doc: "" });
                                }}>
                                {t("rates.cancel")}
                            </div>
                            <div
                                className="updatebtn fontRegular"
                                style={{
                                    marginRight: "0.5rem",
                                    fontSize: "10px",
                                }}
                                onClick={regForm.handleSubmit}>
                                {updatingRegistration ? t("rates.saving") : t("rates.save")}
                            </div>
                        </>
                    ) : (
                        <div
                            className="updatebtn fontRegular"
                            style={{
                                marginRight: "0.5rem",
                                fontSize: "10px",
                            }}
                            onClick={() => {
                                setEditingRegistration(true);
                            }}>
                            {t("rates.update")}
                        </div>
                    )}
                </div> */}
            </div>

            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                }}>
                {editingRegistration ? (
                    <form
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            fontSize: "0.9rem",
                            justifyContent: "space-between",
                        }}
                        onSubmit={regForm.handleSubmit}>
                        <table
                            className="fontRegular"
                            style={{
                                width: "65%",
                            }}>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("locations.state")}: </span>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        name="state"
                                        onChange={regForm.handleChange}
                                        value={regForm.values?.state}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            backgroundColor: "#f2f2fa",
                                            outline: "none",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.expDate")}: </span>
                                </td>
                                <td>
                                    <input
                                        type="date"
                                        name="exp"
                                        onChange={regForm.handleChange}
                                        value={regForm.values?.exp}
                                        style={{
                                            fontSize: "0.9rem",
                                            width: "100%",
                                            backgroundColor: "#f2f2fa",
                                            outline: "none",
                                            border: 0,
                                            borderRadius: "0.5rem",
                                        }}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginTop: "10px",
                                            marginBottom: "5px",
                                            gap: "5px",
                                        }}
                                        onClick={() => {
                                            const butt1 = document.getElementById("registrationInput");
                                            butt1.click();
                                        }}>
                                        {" "}
                                        <input
                                            type="file"
                                            id="registrationInput"
                                            className="file-input-scan-copy"
                                            accept="application/pdf"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                if (e.target.files?.length) {
                                                    setRegistrationDoc(e.target.files[0]);
                                                }
                                            }}
                                        />
                                        <div
                                            className="updatebtn"
                                            style={{
                                                marginRight: "0.5rem",
                                                fontSize: "10px",
                                            }}>
                                            {t("carDetail.scanCopy")}
                                        </div>
                                        <span>
                                            {registrationDoc ? t("carDetail.fileSelected") : t("carDetail.uploadFile")}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </form>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            fontSize: "0.9rem",
                            justifyContent: "space-between",
                        }}>
                        <table
                            className="fontRegular"
                            style={{
                                fontSize: "0.9rem",
                                width: "65%",
                                marginTop: "0.3rem",
                            }}>
                            {/* <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("locations.state")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>{car?.registration?.state}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span style={{ color: "#acacb9" }}>{t("carDetail.expDate")}: </span>
                                </td>
                                <td>
                                    <span style={{ width: "100%" }}>
                                        {car?.registration?.exp
                                            ? new Date(car?.registration?.exp).toLocaleDateString()
                                            : ""}
                                    </span>
                                </td>
                            </tr> */}
                            {car?.registration && (
                                <tr>
                                    <td>
                                        <Tooltip title={t("carDetail.previewDocument")} placement="topLeft">
                                            <a
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    textDecoration: "none",
                                                }}
                                                href={car?.registration?.doc}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="space-x-2 inline-block">
                                                <div style={{ fontSize: "0.8rem" }}>{t("carDetail.viewDocument")}</div>
                                                <FaRegFilePdf />
                                            </a>
                                        </Tooltip>
                                    </td>
                                </tr>
                            )}
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

InsuranceTab.propTypes = {
    car: PropTypes.shape({
        id: PropTypes.string,
        VIN: PropTypes.string,
        insurance: PropTypes.shape({
            company: PropTypes.string,
            policyNo: PropTypes.string,
            coverage: PropTypes.string,
            exp: PropTypes.string,
            doc: PropTypes.string,
        }),
        insuranceVehicleInfo: PropTypes.shape({
            active: PropTypes.bool,
        }),
        abiVehicleInfo: PropTypes.shape({
            registeredOnPeriodX: PropTypes.bool,
            registeredAt: PropTypes.string,
            locationId: PropTypes.number,
        }),
        registration: PropTypes.shape({
            state: PropTypes.string,
            exp: PropTypes.string,
            doc: PropTypes.string,
        }),
    }),
    isLoading: PropTypes.bool,
    currentLocation: PropTypes.shape({
        locationId: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        isInsuranceEnabled: PropTypes.bool,
    }),
    isInsuranceEnabled: PropTypes.bool,
    isAbiInsuranceEnabled: PropTypes.bool,
};

RegistrationTab.propTypes = {
    car: PropTypes.shape({
        id: PropTypes.string,
        registration: PropTypes.shape({
            state: PropTypes.string,
            exp: PropTypes.string,
            doc: PropTypes.string,
        }),
    }),
};
